import {
  BULK_METRO_STATUS_MAP,
  BULK_METRO_STEPS,
  type MetroStepState,
} from "./bulkMetroMap.config";

import type { PaymentMetromap } from "../../types/bulkPaymentsPayroll";

// ---------------------------------------------------------------------------
// OUTPUT TYPES
// ---------------------------------------------------------------------------

export interface MetroStepResolved {
  key: string;
  label: string;
  state: MetroStepState;
  date?: string;
  subItems?: MetroSubItem[];
  show: boolean;
}

export interface MetroSubItem {
  name: string;
  state: MetroStepState;
  date?: string;
  remarks?: string;
}

export interface BulkMetroMapResolved {
  steps: MetroStepResolved[];
  bulkTypeLabel: string;
  approvalsRequired?: number;
}

// ---------------------------------------------------------------------------
// LABEL HELPERS
// ---------------------------------------------------------------------------

const STEP_DEFAULT_LABELS: Record<string, string> = {
  initiated: "Bulk {type} Initiated",
  approval: "Approval In Progress",
  processed: "{type} Processed",
};

const resolveLabel = (
  labelKey: string,
  overrideLabel: string | undefined,
  bulkTypeLabel: string,
): string => {
  if (overrideLabel) return overrideLabel;
  return (STEP_DEFAULT_LABELS[labelKey] ?? labelKey).replace(
    "{type}",
    bulkTypeLabel,
  );
};

// ---------------------------------------------------------------------------
// STEP STATE RESOLUTION
// ---------------------------------------------------------------------------

const resolveStepState = (
  stepKey: string,
  currentStepKey: string,
  completedStepKeys: string[],
  failedStepKeys: string[],
): MetroStepState => {
  if (failedStepKeys.includes(stepKey)) return "failed";
  if (completedStepKeys.includes(stepKey)) return "completed";
  if (stepKey === currentStepKey) return "active";
  return "pending";
};

// ---------------------------------------------------------------------------
// SUB-ITEM EXTRACTION
// API shape: { approvedName, approvedAt, approvedStatus, reason, aprdLevel, aprddById }
// approvedStatus comes back as full words ("Approved" / "Pending" / "Rejected"),
// not the "A"/"R" record codes - both are handled for backward compatibility.
// Fallbacks kept for older shape: approverName, approvalDate, status, remarks
// ---------------------------------------------------------------------------

const extractApprovalSubItems = (
  metromap: PaymentMetromap | null | undefined,
): MetroSubItem[] => {
  const approvers = (metromap?.approvalDetails as any)?.approvers;
  if (!Array.isArray(approvers) || approvers.length === 0) return [];

  return approvers.map(
    (a: any): MetroSubItem => ({
      name: a.approvedName ?? a.approverName ?? "",
      state: resolveApproverState(a.approvedStatus ?? a.status),
      date: a.approvedAt ?? a.approvalDate ?? undefined,
      remarks: a.reason ?? a.remarks ?? undefined,
    }),
  );
};

const resolveApproverState = (rawStatus: string): MetroStepState => {
  switch (rawStatus?.toString().trim().toLowerCase()) {
    case "a":
    case "approved":
      return "completed";
    case "r":
    case "rejected":
      return "failed";
    default:
      return "pending";
  }
};

// ---------------------------------------------------------------------------
// MAIN HELPER
// ---------------------------------------------------------------------------

export const getBulkMetroMapResolved = ({
  jobStatusCode,
  jobStatusDesc,
  paymentMetromap,
  payrollMetromap,
  bulkType = "PAYMENT",
}: {
  jobStatusCode: number | string;
  jobStatusDesc?: string;
  paymentMetromap?: PaymentMetromap | null;
  payrollMetromap?: PaymentMetromap | null;
  bulkType?: string;
}): BulkMetroMapResolved | null => {
  const activeMetromap: PaymentMetromap | null | undefined =
    bulkType === "PAYROLL"
      ? (payrollMetromap ?? paymentMetromap)
      : (paymentMetromap ?? payrollMetromap);

  if (!activeMetromap) return null;

  const code = Number(jobStatusCode);
  const stageConfig = BULK_METRO_STATUS_MAP[code];

  const effectiveConfig = stageConfig ?? {
    currentStepKey: "initiated",
    completedStepKeys: [],
    failedStepKeys: [],
    showProcessed: false,
  };

  const {
    currentStepKey,
    completedStepKeys,
    failedStepKeys = [],
    showProcessed = false,
    approvalLabel,
  } = effectiveConfig;

  const bulkTypeLabel = bulkType === "PAYROLL" ? "Payroll" : "Payments";
  const initiated = activeMetromap.initiatedDetails;
  const resolvedApprovalLabel = jobStatusDesc ?? approvalLabel;

  // Read directly off whichever metromap (payment/payroll) is active for this
  // record, instead of always reading paymentMetromap - fixes payroll flow
  // always falling back to subItems.length in the "X of Y Approvals" header.
  const approvalsRequired: number | undefined = (
    activeMetromap.approvalDetails as any
  )?.approvalsRequired;

  const steps: MetroStepResolved[] = BULK_METRO_STEPS.filter((stepDef) => {
    if (stepDef.isConditional && stepDef.key === "processed") {
      // Only show the Processed card once the backend has actually populated
      // completionDetails - the status-code config saying "showProcessed" is
      // not enough on its own, since debit/credit/completion are filled in
      // progressively and currently come back null.
      return showProcessed && !!activeMetromap.completionDetails;
    }
    return true;
  }).map((stepDef): MetroStepResolved => {
    // === Initiated step ====================================================
    if (stepDef.key === "initiated") {
      return {
        key: "initiated",
        label: resolveLabel("initiated", undefined, bulkTypeLabel),
        state: "completed",
        date: initiated?.initiatedAt ?? undefined,
        show: true,
        subItems: initiated?.initiatedBy?.trim()
          ? [{ name: initiated.initiatedBy.trim(), state: "completed" }]
          : [],
      };
    }

    const state = resolveStepState(
      stepDef.key,
      currentStepKey,
      completedStepKeys,
      failedStepKeys,
    );

    // === Approval step =====================================================
    if (stepDef.key === "approval") {
      return {
        key: "approval",
        label: resolveLabel("approval", resolvedApprovalLabel, bulkTypeLabel),
        state,
        show: true,
        subItems: extractApprovalSubItems(activeMetromap),
      };
    }

    // === Processed step ====================================================
    return {
      key: "processed",
      label: resolveLabel("processed", undefined, bulkTypeLabel),
      state,
      date:
        state === "completed"
          ? (activeMetromap.completionDetails?.completedAt ?? undefined)
          : undefined,
      show: true,
      subItems: [],
    };
  });

  return { steps, bulkTypeLabel, approvalsRequired };
};

// import {
//   BULK_METRO_STATUS_MAP,
//   BULK_METRO_STEPS,
//   type MetroStepState,
// } from "./bulkMetroMap.config";

// import type { PaymentMetromap } from "../../types/bulkPaymentsPayroll";

// // ---------------------------------------------------------------------------
// // OUTPUT TYPES
// // ---------------------------------------------------------------------------

// export interface MetroStepResolved {
//   key: string;
//   label: string;
//   state: MetroStepState;
//   date?: string;
//   subItems?: MetroSubItem[];
//   show: boolean;
// }

// export interface MetroSubItem {
//   name: string;
//   state: MetroStepState;
//   date?: string;
//   remarks?: string;
// }

// export interface BulkMetroMapResolved {
//   steps: MetroStepResolved[];
//   bulkTypeLabel: string;
// }

// // ---------------------------------------------------------------------------
// // LABEL HELPERS
// // ---------------------------------------------------------------------------

// const STEP_DEFAULT_LABELS: Record<string, string> = {
//   initiated: "Bulk {type} Initiated",
//   approval: "Approval In Progress",
//   processed: "{type} Processed",
// };

// const resolveLabel = (
//   labelKey: string,
//   overrideLabel: string | undefined,
//   bulkTypeLabel: string,
// ): string => {
//   if (overrideLabel) return overrideLabel;
//   return (STEP_DEFAULT_LABELS[labelKey] ?? labelKey).replace(
//     "{type}",
//     bulkTypeLabel,
//   );
// };

// // ---------------------------------------------------------------------------
// // STEP STATE RESOLUTION
// // ---------------------------------------------------------------------------

// const resolveStepState = (
//   stepKey: string,
//   currentStepKey: string,
//   completedStepKeys: string[],
//   failedStepKeys: string[],
// ): MetroStepState => {
//   if (failedStepKeys.includes(stepKey)) return "failed";
//   if (completedStepKeys.includes(stepKey)) return "completed";
//   if (stepKey === currentStepKey) return "active";
//   return "pending";
// };

// // ---------------------------------------------------------------------------
// // SUB-ITEM EXTRACTION
// // API shape: { approvedName, approvedAt, approvedStatus, reason, aprdLevel, aprddById }
// // Fallbacks kept for older shape: approverName, approvalDate, status, remarks
// // ---------------------------------------------------------------------------

// const extractApprovalSubItems = (
//   metromap: PaymentMetromap | null | undefined,
// ): MetroSubItem[] => {
//   const approvers = (metromap?.approvalDetails as any)?.approvers;
//   if (!Array.isArray(approvers) || approvers.length === 0) return [];

//   return approvers.map(
//     (a: any): MetroSubItem => ({
//       name: a.approvedName ?? a.approverName ?? "",
//       state: resolveApproverState(a.approvedStatus ?? a.status),
//       date: a.approvedAt ?? a.approvalDate ?? undefined,
//       remarks: a.reason ?? a.remarks ?? undefined,
//     }),
//   );
// };

// const resolveApproverState = (rawStatus: string): MetroStepState => {
//   switch (rawStatus) {
//     case "A":
//       return "completed";
//     case "R":
//       return "failed";
//     default:
//       return "pending";
//   }
// };

// // ---------------------------------------------------------------------------
// // MAIN HELPER
// // ---------------------------------------------------------------------------

// export const getBulkMetroMapResolved = ({
//   jobStatusCode,
//   jobStatusDesc,
//   paymentMetromap,
//   payrollMetromap,
//   bulkType = "PAYMENT",
// }: {
//   jobStatusCode: number | string;
//   jobStatusDesc?: string;
//   paymentMetromap?: PaymentMetromap | null;
//   payrollMetromap?: PaymentMetromap | null;
//   bulkType?: string;
// }): BulkMetroMapResolved | null => {
//   const activeMetromap: PaymentMetromap | null | undefined =
//     bulkType === "PAYROLL"
//       ? (payrollMetromap ?? paymentMetromap)
//       : (paymentMetromap ?? payrollMetromap);

//   if (!activeMetromap) return null;

//   const code = Number(jobStatusCode);
//   const stageConfig = BULK_METRO_STATUS_MAP[code];

//   const effectiveConfig = stageConfig ?? {
//     currentStepKey: "initiated",
//     completedStepKeys: [],
//     failedStepKeys: [],
//     showProcessed: false,
//   };

//   const {
//     currentStepKey,
//     completedStepKeys,
//     failedStepKeys = [],
//     showProcessed = false,
//     approvalLabel,
//   } = effectiveConfig;

//   const bulkTypeLabel = bulkType === "PAYROLL" ? "Payroll" : "Payments";
//   const initiated = activeMetromap.initiatedDetails;
//   const resolvedApprovalLabel = jobStatusDesc ?? approvalLabel;
//   const steps: MetroStepResolved[] = BULK_METRO_STEPS.filter((stepDef) => {
//     if (stepDef.isConditional && stepDef.key === "processed") {
//       return showProcessed;
//     }
//     return true;
//   }).map((stepDef): MetroStepResolved => {
//     // === Initiated step ====================================================
//     if (stepDef.key === "initiated") {
//       return {
//         key: "initiated",
//         label: resolveLabel("initiated", undefined, bulkTypeLabel),
//         state: "completed",
//         date: initiated?.initiatedAt ?? undefined,
//         show: true,
//         subItems: initiated?.initiatedBy?.trim()
//           ? [{ name: initiated.initiatedBy.trim(), state: "completed" }]
//           : [],
//       };
//     }

//     const state = resolveStepState(
//       stepDef.key,
//       currentStepKey,
//       completedStepKeys,
//       failedStepKeys,
//     );

//     // === Approval step =====================================================
//     if (stepDef.key === "approval") {
//       return {
//         key: "approval",
//         label: resolveLabel("approval", resolvedApprovalLabel, bulkTypeLabel),
//         state,
//         show: true,
//         subItems: extractApprovalSubItems(activeMetromap),
//       };
//     }

//     // === Processed step ====================================================
//     return {
//       key: "processed",
//       label: resolveLabel("processed", undefined, bulkTypeLabel),
//       state,
//       date:
//         state === "completed"
//           ? ((activeMetromap.completionDetails as any)?.completedAt ??
//             undefined)
//           : undefined,
//       show: true,
//       subItems: [],
//     };
//   });

//   return { steps, bulkTypeLabel };
// };
